import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { useLanguage } from '../hooks/useLanguage';
interface PrivacyModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function PrivacyModal({
  isOpen,
  onClose
}: PrivacyModalProps) {
  const {
    language
  } = useLanguage();
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);
  const accentColor = '#00d4ff'; // Cyan
  const content = {
    fr: {
      title: 'POLITIQUE DE CONFIDENTIALITÉ',
      lastUpdated: 'Dernière mise à jour : 15 janvier 2026',
      sections: [{
        title: '1. Introduction',
        content: 'Tuiit ("nous", "notre", "nos") s\'engage à protéger et respecter votre vie privée. Cette politique de confidentialité explique comment nous collectons, utilisons, partageons et protégeons vos informations personnelles lorsque vous utilisez nos services de création de sites web IA, d\'hébergement et de solutions IT.'
      }, {
        title: '2. Informations que nous collectons',
        content: "Nous collectons les types d'informations suivants :\n\n• Informations d'identification : nom, adresse e-mail, numéro de téléphone, nom de l'entreprise\n• Informations techniques : adresse IP, type de navigateur, système d'exploitation, données de cookies\n• Informations de projet : détails sur vos besoins en matière de site web, budget, préférences techniques\n• Données d'utilisation : comment vous interagissez avec nos services et notre site web"
      }, {
        title: '3. Comment nous utilisons vos informations',
        content: 'Nous utilisons vos informations pour :\n\n• Fournir et améliorer nos services de développement web et IT\n• Communiquer avec vous concernant vos projets et nos services\n• Traiter vos demandes et transactions\n• Envoyer des mises à jour techniques et des notifications de sécurité\n• Analyser et améliorer nos services\n• Respecter nos obligations légales'
      }, {
        title: '4. Partage de vos informations',
        content: "Nous ne vendons pas vos informations personnelles. Nous pouvons partager vos informations avec :\n\n• Prestataires de services : fournisseurs d'hébergement cloud (AWS, Azure, GCP), outils d'analyse\n• Partenaires commerciaux : uniquement avec votre consentement explicite\n• Autorités légales : lorsque requis par la loi ou pour protéger nos droits"
      }, {
        title: '5. Sécurité des données',
        content: "Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos informations personnelles contre l'accès non autorisé, la perte ou la destruction. Cela inclut le chiffrement, les contrôles d'accès et les audits de sécurité réguliers."
      }, {
        title: '6. Vos droits',
        content: 'Vous avez le droit de :\n\n• Accéder à vos informations personnelles\n• Rectifier des informations inexactes\n• Demander la suppression de vos données\n• Vous opposer au traitement de vos données\n• Demander la portabilité de vos données\n• Retirer votre consentement à tout moment'
      }, {
        title: '7. Cookies et technologies de suivi',
        content: "Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience, analyser l'utilisation du site et personnaliser le contenu. Vous pouvez gérer vos préférences en matière de cookies via les paramètres de votre navigateur."
      }, {
        title: '8. Conservation des données',
        content: 'Nous conservons vos informations personnelles aussi longtemps que nécessaire pour fournir nos services et respecter nos obligations légales. Les données de projet sont généralement conservées pendant la durée de notre relation commerciale plus 7 ans pour des raisons légales.'
      }, {
        title: '9. Transferts internationaux',
        content: "Vos informations peuvent être transférées et traitées en Nouvelle-Zélande et dans d'autres pays où nous ou nos prestataires de services opérons. Nous assurons que ces transferts respectent les lois applicables en matière de protection des données."
      }, {
        title: '10. Modifications de cette politique',
        content: 'Nous pouvons mettre à jour cette politique de confidentialité périodiquement. Nous vous informerons de tout changement significatif par e-mail ou via une notification sur notre site web.'
      }, {
        title: '11. Nous contacter',
        content: 'Pour toute question concernant cette politique de confidentialité ou vos données personnelles, contactez-nous à :\n\nTuiit\nEmail : privacy@tuiit.nz\nAdresse : Nouvelle-Zélande'
      }]
    },
    en: {
      title: 'PRIVACY POLICY',
      lastUpdated: 'Last updated: January 15, 2026',
      sections: [{
        title: '1. Introduction',
        content: 'Tuiit ("we", "our", "us") is committed to protecting and respecting your privacy. This privacy policy explains how we collect, use, share, and protect your personal information when you use our AI website creation, hosting, and IT solutions services.'
      }, {
        title: '2. Information We Collect',
        content: 'We collect the following types of information:\n\n• Identification information: name, email address, phone number, company name\n• Technical information: IP address, browser type, operating system, cookie data\n• Project information: details about your website needs, budget, technical preferences\n• Usage data: how you interact with our services and website'
      }, {
        title: '3. How We Use Your Information',
        content: 'We use your information to:\n\n• Provide and improve our web development and IT services\n• Communicate with you about your projects and our services\n• Process your requests and transactions\n• Send technical updates and security notifications\n• Analyze and improve our services\n• Comply with our legal obligations'
      }, {
        title: '4. Sharing Your Information',
        content: 'We do not sell your personal information. We may share your information with:\n\n• Service providers: cloud hosting providers (AWS, Azure, GCP), analytics tools\n• Business partners: only with your explicit consent\n• Legal authorities: when required by law or to protect our rights'
      }, {
        title: '5. Data Security',
        content: 'We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, loss, or destruction. This includes encryption, access controls, and regular security audits.'
      }, {
        title: '6. Your Rights',
        content: 'You have the right to:\n\n• Access your personal information\n• Rectify inaccurate information\n• Request deletion of your data\n• Object to processing of your data\n• Request data portability\n• Withdraw your consent at any time'
      }, {
        title: '7. Cookies and Tracking Technologies',
        content: 'We use cookies and similar technologies to enhance your experience, analyze site usage, and personalize content. You can manage your cookie preferences through your browser settings.'
      }, {
        title: '8. Data Retention',
        content: 'We retain your personal information for as long as necessary to provide our services and comply with our legal obligations. Project data is typically retained for the duration of our business relationship plus 7 years for legal reasons.'
      }, {
        title: '9. International Transfers',
        content: 'Your information may be transferred to and processed in New Zealand and other countries where we or our service providers operate. We ensure these transfers comply with applicable data protection laws.'
      }, {
        title: '10. Changes to This Policy',
        content: 'We may update this privacy policy periodically. We will notify you of any significant changes by email or through a notification on our website.'
      }, {
        title: '11. Contact Us',
        content: 'For any questions about this privacy policy or your personal data, contact us at:\n\nTuiit\nEmail: privacy@tuiit.nz\nAddress: New Zealand'
      }]
    }
  };
  const currentContent = language === 'fr' ? content.fr : content.en;
  return <AnimatePresence data-id="element-244">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-lg overflow-hidden pt-16 sm:pt-0 p-4" onClick={onClose} data-id="element-245">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.2
      }} onClick={e => e.stopPropagation()} className="relative w-full max-w-4xl h-full sm:h-auto sm:max-h-[85vh] bg-dark-card border border-white/10 overflow-hidden" style={{
        boxShadow: `0 0 60px ${accentColor}40, 0 0 100px ${accentColor}20, inset 0 0 40px ${accentColor}08`
      }} data-id="element-246">
            {/* Corner Accents */}
            <div className="absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 pointer-events-none" data-id="element-247">
              <div className="absolute top-0 right-0 w-full h-0.5" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-248" />
              <div className="absolute top-0 right-0 w-0.5 h-full" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-249" />
            </div>
            <div className="absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8 pointer-events-none" data-id="element-250">
              <div className="absolute bottom-0 left-0 w-full h-0.5" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-251" />
              <div className="absolute bottom-0 left-0 w-0.5 h-full" style={{
            background: accentColor,
            boxShadow: `0 0 10px ${accentColor}, 0 0 20px ${accentColor}80`
          }} data-id="element-252" />
            </div>

            {/* Header */}
            <div className="sticky top-0 z-10 bg-dark-card border-b border-white/10 px-4 sm:px-8 py-4 sm:py-6" data-id="element-253">
              <button onClick={onClose} className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-500 hover:text-cyan transition-colors" aria-label="Close" data-id="element-254">
                <X size={20} className="sm:w-6 sm:h-6" data-id="element-255" />
              </button>

              <h2 className="font-cyber text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2" data-id="element-256">
                {currentContent.title}
              </h2>
              <p className="text-xs sm:text-sm text-gray-500 font-mono" data-id="element-257">
                {currentContent.lastUpdated}
              </p>
            </div>

            {/* Content */}
            <div className="overflow-y-auto h-[calc(100%-100px)] sm:h-auto sm:max-h-[calc(85vh-120px)] px-4 sm:px-8 py-6" data-id="element-258">
              <div className="space-y-6 sm:space-y-8" data-id="element-259">
                {currentContent.sections.map((section, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: index * 0.05
            }} data-id="element-260">
                    <h3 className="font-cyber text-base sm:text-lg font-bold text-cyan mb-3" data-id="element-261">
                      {section.title}
                    </h3>
                    <div className="text-sm sm:text-base text-gray-300 leading-relaxed whitespace-pre-line" data-id="element-262">
                      {section.content}
                    </div>
                  </motion.div>)}
              </div>

              {/* Bottom spacing */}
              <div className="h-8" data-id="element-263" />
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}